import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ARENA_RULES, HOUSE_BOTS } from "@buildlab/shared/arena";
import { arenaApi, dayLabel, type ArenaEntryInfo, type ArenaHome, type ArenaMatchInfo, type ArenaRow, type ArenaStandings } from "./api";
import { ArenaReplay } from "./ArenaReplay";
import { useMe } from "./me";
import { Avatar } from "./Sprite";
import "./Golf.css";
import "./Arena.css";

// Bot Arena on Home (decision 99, Review 21): students don't play Coin Grab, they tell the helper
// how their bot should play. "Build my bot" opens the bot project in the normal workspace (a
// practice match in the preview); "Enter my bot" there sends a copy of bot.js to the class
// tournament; "Standings" shows the latest table and replays of your own matches. Students can
// open it from Meeting 7 (Wed Nov 4); teachers any time, and they run tournaments on #arena.

const when = (t: number) => new Date(t).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });

export const isArenaProject = (assignmentId: string | null | undefined): boolean => !!assignmentId?.startsWith("arena:");

export function BotArenaEntry({ onOpen }: { onOpen: (projectId: string) => void }) {
  const me = useMe();
  const teacher = me?.user.role === "teacher";
  const [data, setData] = useState<ArenaHome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [standings, setStandings] = useState(false);

  useEffect(() => {
    arenaApi.home().then(setData, (e: Error) => setError(e.message));
  }, []);
  const locked = !!data && !data.open;

  const build = async () => {
    setBusy(true);
    try {
      onOpen((await arenaApi.bot()).projectId);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <section className="arena-home" aria-label="Bot Arena">
      <div className="arena-home-head">
        <span className="arena-flag" aria-hidden="true">
          🤖
        </span>
        <div>
          <h2>Bot Arena</h2>
          <p className="small">
            You don't play this game: your bot does. Tell the helper how your bot should play Coin Grab, test it in a practice match, then enter it in the class tournament. Watch the replays and make it better.
          </p>
        </div>
        {locked && (
          <span className="arena-lock" role="status">
            <span aria-hidden="true">🔒</span> Opens {dayLabel(data!.opensOn)}
          </span>
        )}
      </div>
      {error && <p className="page-error small">{error}</p>}
      <details className="arena-rules">
        <summary>How Coin Grab works</summary>
        <ul>
          {ARENA_RULES.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </details>
      {data && (
        <div className="arena-home-row">
          <button type="button" className={locked ? "outline" : "primary"} disabled={locked || busy} onClick={() => void build()}>
            {locked ? "Locked" : data.bot ? "Open my bot" : "Build my bot"}
          </button>
          <span className="arena-status small">
            {locked ? (
              ""
            ) : data.entry ? (
              <>
                Entered: <b>{data.entry.botName}</b> · {when(data.entry.updatedAt)}
                {data.entry.status === "held" && <span className="arena-held"> · waiting for your teacher</span>}
              </>
            ) : (
              <span className="muted">Not entered yet. Open your bot and press Enter my bot.</span>
            )}
            {data.latest && (
              <span className="muted">
                {" "}
                · Last tournament: {data.latest.rank ? `you came #${data.latest.rank} of ${data.latest.bots}` : `${data.latest.bots} bots`}
              </span>
            )}
          </span>
          {!locked && (
            <button type="button" className="outline small" onClick={() => setStandings(true)}>
              Standings
            </button>
          )}
          {teacher && (
            <button type="button" className="outline small" onClick={() => (window.location.hash = "arena")}>
              Run a tournament
            </button>
          )}
        </div>
      )}
      {teacher && data && <p className="small muted arena-teacher-note">Students can open it from Meeting 7 ({dayLabel(data.opensOn)}). Helper messages cost coins as usual; matches cost nothing.</p>}
      {standings && <ArenaStandingsDialog onClose={() => setStandings(false)} />}
    </section>
  );
}

/** The table: rank, player, bot, W-D-L, coins, points. */
export function ArenaTable({ table }: { table: ArenaRow[] }) {
  return (
    <ol className="arena-table">
      <li className="arena-row head" aria-hidden="true">
        <span className="r">#</span>
        <span className="n">Bot</span>
        <span className="wdl">W-D-L</span>
        <span className="c">Coins</span>
        <span className="p">Points</span>
      </li>
      {table.map((r, i) => (
        <li key={i} className={`arena-row${r.me ? " me" : ""}${r.rank === 1 ? " first" : ""}`}>
          <span className="r">{r.rank}</span>
          <span className="n">
            <b className="bn">{r.botName}</b>
            <span className="who small">
              {!r.house && <Avatar name={r.name} avatar={r.avatar} size={14} />}
              {r.name}
              {r.me ? " (you)" : ""}
            </span>
          </span>
          <span className="wdl">
            {r.wins}-{r.draws}-{r.losses}
          </span>
          <span className="c">{r.coins}</span>
          <span className="p">{r.points}</span>
        </li>
      ))}
    </ol>
  );
}

/** A list of matches with a Watch button each. */
export function ArenaMatchList({ matches, onWatch, limit }: { matches: ArenaMatchInfo[]; onWatch: (m: ArenaMatchInfo) => void; limit?: number }) {
  const shown = limit ? matches.slice(0, limit) : matches;
  return (
    <ul className="arena-matches">
      {shown.map((m) => (
        <li key={m.id}>
          <span className={`side${m.winner === "a" ? " won" : ""}`}>
            🤖 {m.a.botName}
            {m.a.me ? " (you)" : ""}
          </span>
          <span className="score">
            {m.aCoins}–{m.bCoins}
          </span>
          <span className={`side${m.winner === "b" ? " won" : ""}`}>
            👾 {m.b.botName}
            {m.b.me ? " (you)" : ""}
          </span>
          <button type="button" className="linklike small" onClick={() => onWatch(m)} aria-label={`Watch ${m.a.botName} vs ${m.b.botName}`}>
            Watch
          </button>
        </li>
      ))}
    </ul>
  );
}

/** A student's view of the latest tournament (teachers get it too, for their default class). */
export function ArenaStandingsDialog({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<ArenaStandings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [watch, setWatch] = useState<string | null>(null);
  useEffect(() => {
    arenaApi.standings().then(setData, (e: Error) => setError(e.message));
  }, []);
  useEffect(() => {
    if (watch) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [watch, onClose]);
  return (
    <div className="golf-card-backdrop" role="dialog" aria-modal="true" aria-label="Bot Arena standings" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="golf-board arena-standings">
        <div className="row between golf-board-head">
          <div>
            <span className="eyebrow">Bot Arena · {data?.className ?? "Your class"}</span>
            <h2>Standings</h2>
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        {error && <p className="page-error small">{error}</p>}
        {data && !data.tournament && <p className="golf-board-empty">No tournament yet. Enter your bot, and your teacher will run one.</p>}
        {data?.tournament && (
          <>
            <p className="small muted">
              {when(data.tournament.createdAt)} · {data.tournament.bots} bots · everyone played everyone twice. A win is 3 points and a draw is 1.
            </p>
            <ArenaTable table={data.table} />
            <h3 className="arena-sub">{data.teacher ? "Matches" : "Your matches"}</h3>
            {data.matches.length === 0 ? <p className="small muted">Your bot wasn't in this one.</p> : <ArenaMatchList matches={data.matches} onWatch={(m) => setWatch(m.id)} />}
          </>
        )}
      </div>
      {watch && <ArenaReplay matchId={watch} onClose={() => setWatch(null)} />}
    </div>
  );
}

/** "Enter my bot" in the bot's workspace: a copy of bot.js as it is now goes to the class tournament. */
export function ArenaEnterButton({ projectId, title, disabled, beforeOpen }: { projectId: string; title: string; disabled: boolean; beforeOpen: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [entry, setEntry] = useState<ArenaEntryInfo | null>(null);
  const [done, setDone] = useState<ArenaEntryInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const show = async () => {
    await beforeOpen();
    setDone(null);
    setError(null);
    // The last entry's name first, so nothing typed gets replaced when it arrives.
    try {
      const home = await arenaApi.home();
      setEntry(home.entry);
      setName(home.entry?.botName ?? title.replace(/^My /, "").replace(/ bot$/, "") + " Bot");
    } catch (e) {
      setError((e as Error).message);
    }
    setOpen(true);
  };
  const enter = async () => {
    setBusy(true);
    setError(null);
    try {
      await beforeOpen();
      setDone((await arenaApi.enter(projectId, name)).entry);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button type="button" className="primary arena-enter-btn" disabled={disabled} onClick={() => void show()}>
        <span aria-hidden="true">🏆</span> <span className="bar-label">Enter my bot</span>
      </button>
      {/* Over the whole page, not inside the top bar (whose white text it would take on). */}
      {open &&
        createPortal(
          <div className="golf-card-backdrop" role="dialog" aria-modal="true" aria-label="Enter my bot" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
            <div className="golf-board arena-enter">
              <div className="row between golf-board-head">
                <div>
                  <span className="eyebrow">Bot Arena</span>
                  <h2>Enter my bot</h2>
                </div>
                <button type="button" className="icon-close" aria-label="Close" onClick={() => setOpen(false)}>
                  ×
                </button>
              </div>
              {done ? (
                <>
                  <p className="arena-entered" role="status">
                    <b>{done.botName}</b> is entered!{" "}
                    {done.status === "held" ? "Your teacher will look at it before it plays." : "It will play in the next class tournament."}
                  </p>
                  <p className="small muted">Keep making it better. Enter again any time to send your newest version.</p>
                  <div className="row">
                    <button type="button" className="primary" onClick={() => setOpen(false)} autoFocus>
                      Back to my bot
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="small">
                    This sends a copy of <b>bot.js</b> as it is right now to your class's tournament.{" "}
                    {entry ? `It replaces ${entry.botName}, your entry from ${when(entry.updatedAt)}.` : "You can enter again later to update it."}
                  </p>
                  <label className="arena-name">
                    <span className="small">Bot name</span>
                    <input value={name} maxLength={30} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !busy && void enter()} autoFocus />
                  </label>
                  <p className="small muted">Classmates see your bot's name with your name. They never see your code.</p>
                  {error && <p className="page-error small">{error}</p>}
                  <div className="row">
                    <button type="button" className="primary" disabled={busy || !name.trim()} onClick={() => void enter()}>
                      {busy ? "Entering…" : "Enter my bot"}
                    </button>
                    <button type="button" className="linklike" onClick={() => setOpen(false)}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/** Teacher tools on Home: open the Arena page. */
export function ArenaTeacherEntry() {
  return (
    <section aria-label="Bot Arena tournaments" className="subs-entry">
      <div>
        <h2>Bot Arena</h2>
        <p className="muted small">See each class's bots, run a tournament in this browser, and play the replays on the projector. {HOUSE_BOTS.length} house bots can join a small class.</p>
      </div>
      <button type="button" className="primary" onClick={() => (window.location.hash = "arena")}>
        Open Bot Arena
      </button>
    </section>
  );
}
